import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper
} from '@tanstack/react-table';
import AdminButton from 'components/common/AdminButton';
import { useUser } from 'components/RDAuthContext';
import { useOrgUnitsData, useHierarchyStructure } from '../hooks/useLocationAdminData';
import { API, graphqlOperation } from 'aws-amplify';
import { listUsers } from 'graphql/queries';
import EditOrgUnitModal from '../components/EditOrgUnitModal';
import CreateOrgUnitModal from '../components/CreateOrgUnitModal';

// Create column helper for type safety
const columnHelper = createColumnHelper();

// Table component for each level
function LevelTable({ data, columns }) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  style={{
                    padding: '8px 12px',
                    textAlign: header.column.columnDef.meta?.align || 'left',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    color: '#637381'
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr 
              key={row.id}
              style={{
                borderBottom: '1px solid #f0f0f0',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {row.getVisibleCells().map(cell => (
                <td
                  key={cell.id}
                  style={{
                    padding: '6px 12px',
                    fontSize: '0.8125rem',
                    textAlign: cell.column.columnDef.meta?.align || 'left'
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

function UnitsTab() {
  const { user } = useUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [managersMap, setManagersMap] = useState({});
  const [loadingManagers, setLoadingManagers] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitsByLevel, setUnitsByLevel] = useState({});
  const [filteredUnitsByLevel, setFilteredUnitsByLevel] = useState({});
  const [hierarchyLevels, setHierarchyLevels] = useState([]);
  
  // Fetch org units data using the hook
  const { data: orgUnitsData, isLoading: orgUnitsLoading, refetch: refetchOrgUnits } = useOrgUnitsData();
  const { data: hierarchyData, isLoading: hierarchyLoading } = useHierarchyStructure();
  
  // Extract data from hooks
  const propActiveUnits = orgUnitsData?.activeUnits || [];
  const propInactiveUnits = orgUnitsData?.inactiveUnits || [];
  const hierarchyDefinition = hierarchyData?.structure || [];
  
  // Fetch all users to create a managers map
  useEffect(() => {
    const fetchManagers = async () => {
      if (!user?.client) return;
      
      try {
        setLoadingManagers(true);
        const usersResult = await API.graphql(
          graphqlOperation(listUsers, {
            filter: { client: { eq: user.client } },
            limit: 1000
          })
        );
        
        const users = usersResult?.data?.listUsers?.items || [];
        const userMap = {};
        users.forEach(u => {
          userMap[u.id] = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
        });
        
        // console.log('Managers map created:', Object.keys(userMap).length, 'users');
        setManagersMap(userMap);
      } catch (error) {
        console.error('Error fetching managers:', error);
      } finally {
        setLoadingManagers(false);
      }
    };
    
    fetchManagers();
  }, [user]);

  // Process units and organize by level
  useEffect(() => {
    // console.log('UnitsTab - Processing units:', {
    //   propActiveUnits,
    //   propInactiveUnits
    // });
    
    // Flatten hierarchical active units
    const flattenUnits = (units, parentId = null) => {
      const flattened = [];
      units.forEach(unit => {
        const flatUnit = {
          id: unit.id || unit.orgUnitId,
          name: unit.name,
          orgLevel: unit.orgLevel ? `ORGLEVEL_${unit.orgLevel}` : unit.levelType,
          // Handle both manager (name) and managerId fields from the service
          managerId: unit.managerId || unit.managerUserId,
          managerName: unit.manager || unit.managerName || null,
          locationCount: unit.locationCount || 0,
          isActive: unit.isActive !== false, // Preserve the original isActive value
          parentId: parentId,
          parentOrgUnitId: unit.parentOrgUnitId || parentId
        };
        flattened.push(flatUnit);
        
        if (unit.children && unit.children.length > 0) {
          flattened.push(...flattenUnits(unit.children, unit.id));
        }
      });
      return flattened;
    };
    
    // Process active units (might be hierarchical)
    const activeUnitsFlat = propActiveUnits ? flattenUnits(propActiveUnits) : [];
    
    // Process inactive units (already flat)
    const inactiveUnitsFlat = (propInactiveUnits || []).map(unit => {
      return {
        id: unit.historyId || unit.id,
        name: unit.name,
        orgLevel: unit.orgLevel?.startsWith('ORGLEVEL_') ? unit.orgLevel : `ORGLEVEL_${unit.orgLevel}`,
        managerId: unit.managerId || unit.managerUserId || unit.manager,
        managerName: unit.managerName || null,
        locationCount: 0,
        isActive: unit.isActive === true, // Preserve the original isActive value
        parentId: unit.parentOrgUnitId || null,
        parentOrgUnit: unit.parentOrgUnit,
        endDate: unit.endDate,
        changeReason: unit.changeReason,
        changedBy: unit.changedBy
      };
    });
    
    const allUnits = [...activeUnitsFlat, ...inactiveUnitsFlat];

    // Create a map for quick parent lookup
    const unitsMap = {};
    allUnits.forEach(unit => {
      unitsMap[unit.id] = unit;
    });
    
    // Group units by org level
    const grouped = {};
    allUnits.forEach(unit => {
      const level = unit.orgLevel;
      if (!level) return;
      
      if (!grouped[level]) {
        grouped[level] = [];
      }
      grouped[level].push({
        ...unit,
        parent: unit.parentId ? unitsMap[unit.parentId] : null
      });
    });
    
    // Sort units within each level by name
    Object.keys(grouped).forEach(level => {
      grouped[level].sort((a, b) => a.name.localeCompare(b.name));
    });
    
    // console.log('UnitsTab - Grouped by level:', grouped);
    
    setUnitsByLevel(grouped);
    setFilteredUnitsByLevel(grouped);
  }, [propActiveUnits, propInactiveUnits]);
  
  // Extract hierarchy levels from hierarchy definition or units
  useEffect(() => {
    // console.log('UnitsTab - hierarchyDefinition:', hierarchyDefinition);
    
    if (hierarchyDefinition && Array.isArray(hierarchyDefinition)) {
      // hierarchyDefinition is an array of level definitions
      const activeLevels = hierarchyDefinition
        .filter(level => level.levelId && level.levelId.startsWith('ORGLEVEL'))
        .sort((a, b) => b.order - a.order);
      
      // console.log('UnitsTab - Active levels from definition:', activeLevels);
      setHierarchyLevels(activeLevels);
    } else if (hierarchyDefinition && hierarchyDefinition.levels) {
      // hierarchyDefinition has a levels property
      const activeLevels = hierarchyDefinition.levels
        .filter(level => level.isUsed && level.levelId.startsWith('ORGLEVEL'))
        .sort((a, b) => b.order - a.order);
      
      // console.log('UnitsTab - Active levels from definition.levels:', activeLevels);
      setHierarchyLevels(activeLevels);
    } else {
      // Fallback: extract from units data
      const levelIds = Object.keys(unitsByLevel)
        .filter(level => level && level.startsWith('ORGLEVEL'))
        .sort((a, b) => {
          const numA = parseInt(a.replace('ORGLEVEL_', ''));
          const numB = parseInt(b.replace('ORGLEVEL_', ''));
          return numB - numA;
        });
      
      const fallbackLevels = levelIds.map(levelId => {
        const levelNum = parseInt(levelId.replace('ORGLEVEL_', ''));
        let label = 'Level ' + levelNum;
        
        // Try to guess better labels
        if (levelNum === 3) label = 'Region';
        else if (levelNum === 2) label = 'Area';
        else if (levelNum === 1) label = 'District';
        
        return {
          levelId,
          label,
          order: levelNum,
          isUsed: true
        };
      });
      
      // console.log('UnitsTab - Fallback levels from units:', fallbackLevels);
      setHierarchyLevels(fallbackLevels);
    }
  }, [hierarchyDefinition]);

  // Handle search and filter
  useEffect(() => {
    let filtered = {};

    Object.keys(unitsByLevel).forEach(level => {
      let levelUnits = [...unitsByLevel[level]];

      // Apply active filter
      if (showActiveOnly) {
        levelUnits = levelUnits.filter(unit => unit.isActive);
      }
      
      // Apply search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        levelUnits = levelUnits.filter(unit => 
          unit.name.toLowerCase().includes(searchLower) ||
          unit.managerName?.toLowerCase().includes(searchLower) ||
          unit.parent?.name?.toLowerCase().includes(searchLower)
        );
      }
      
      if (levelUnits.length > 0) {
        filtered[level] = levelUnits;
      }
    });
    
    setFilteredUnitsByLevel(filtered);
  }, [searchTerm, unitsByLevel, showActiveOnly]);

  // Create parent units map for quick lookup
  const parentUnitsMap = useMemo(() => {
    const map = {};
    Object.values(unitsByLevel).forEach(levelUnits => {
      levelUnits.forEach(unit => {
        map[unit.id] = unit;
      });
    });
    return map;
  }, [unitsByLevel]);
  
  const handleEdit = async (unit) => {
    setSelectedUnit(unit);
    setEditModalOpen(true);
  };

  const handleCreateUnit = () => {
    setCreateModalOpen(true);
  };
  
  // Define columns for the table
  const columns = useMemo(
    () => {
      // Determine if we should show the parent unit column
      // Only show if client has more than one OrgLevel
      const showParentColumn = hierarchyLevels.length > 1;
      
      const baseColumns = [
        columnHelper.accessor('name', {
          header: 'Name',
          cell: info => (
            <Typography variant="caption" fontWeight="medium">
              {info.getValue()}
            </Typography>
          ),
        }),
        columnHelper.accessor(row => row.managerId || row.managerName, {
          id: 'manager',
          header: 'Manager',
          cell: info => {
            const unit = info.row.original;
            // First check if we have a name, then try to look up by ID
            let displayName = unit.managerName;
            
            if (!displayName && unit.managerId) {
              // Try to look up the manager name from the map
              displayName = managersMap[unit.managerId];
            }
            
            // If still no name but we have an ID, it might be that the user doesn't exist
            // Don't show the ID to avoid confusion
            if (!displayName && unit.managerId) {
              // console.log('Manager lookup failed for ID:', unit.managerId);
              displayName = null;
            }
            
            return (
              <Box display="flex" alignItems="center" gap={0.5}>
                {displayName ? (
                  <>
                    <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text">
                      {displayName}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No manager assigned
                  </Typography>
                )}
              </Box>
            );
          },
        }),
        columnHelper.accessor('isActive', {
          header: 'Status',
          meta: { align: 'center' },
          cell: info => (
            <div style={{ textAlign: 'center' }}>
              <Chip
                label={info.getValue() ? 'Active' : 'Inactive'}
                size="small"
                color={info.getValue() ? 'success' : 'default'}
                variant="outlined"
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
            </div>
          ),
        }),
      ];
      
      // Conditionally add parent unit column
      if (showParentColumn) {
        baseColumns.push(
          columnHelper.accessor('parentId', {
            header: 'Parent Unit',
            cell: info => {
              const parentId = info.getValue();
              const parentUnit = parentId ? parentUnitsMap[parentId] : null;
              return (
                <Typography variant="caption" color="text">
                  {parentUnit ? parentUnit.name : '-'}
                </Typography>
              );
            },
          })
        );
      }
      
      // Always add actions column at the end
      baseColumns.push(
        columnHelper.display({
          id: 'actions',
          header: 'Actions',
          meta: { align: 'right' },
          cell: ({ row }) => (
            <Box
              className="actions"
              display="flex"
              gap={1}
              justifyContent="flex-end"
              sx={{ opacity: 1 }}
            >
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => handleEdit(row.original)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ),
        })
      );
      
      return baseColumns;
    },
    [managersMap, parentUnitsMap, hierarchyLevels]
  );

  if (orgUnitsLoading || hierarchyLoading || loadingManagers) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <Typography variant="body2" color="text">
          Loading organizational units...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Box mb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Organizational Units
            </Typography>
            <Typography variant="body2" color="text">
              Manage organizational hierarchy and assignments
            </Typography>
          </Box>
          <AdminButton
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleCreateUnit}
          >
            Create Unit
          </AdminButton>
        </Box>

        {/* Search and Filter Bar */}
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            placeholder="Search units or managers..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, maxWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={showActiveOnly}
                onChange={(e) => setShowActiveOnly(e.target.checked)}
                color="primary"
              />
            }
            label="Active Only"
          />
        </Box>
      </Box>

      {/* Debug info - remove after testing */}
      {/* {!parentLoading && (
        <Box mb={2} p={2} bgcolor="grey.100" borderRadius={1}>
          <Typography variant="caption" color="text">
            Debug: hierarchyLevels={hierarchyLevels.length}, unitsByLevel={Object.keys(unitsByLevel).length}, 
            filteredUnitsByLevel={Object.keys(filteredUnitsByLevel).length}
          </Typography>
        </Box>
      )} */}
      
      {/* Display units grouped by OrgLevel in descending order */}
      {hierarchyLevels.length === 0 && Object.keys(filteredUnitsByLevel).length === 0 ? (
        <Card>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <Typography variant="body2" color="text">
              {searchTerm ? 'No units found matching your search' : 'No organizational units found'}
            </Typography>
          </Box>
        </Card>
      ) : (
        hierarchyLevels.map(level => {
          const levelUnits = filteredUnitsByLevel[level.levelId] || [];
          
          // Skip levels with no units if client doesn't use them
          if (levelUnits.length === 0 && !searchTerm) {
            return null;
          }
          
          return (
            <Card key={level.levelId} sx={{ mb: 3 }}>
              <Box p={2}>
                <Typography variant="h6" fontWeight="medium" mb={2}>
                  {level.label}
                </Typography>
                
                {levelUnits.length === 0 ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={100}>
                    <Typography variant="body2" color="text">
                      No {level.label.toLowerCase()} found
                    </Typography>
                  </Box>
                ) : (
                  <LevelTable 
                    data={levelUnits}
                    columns={columns}
                  />
                )}
              </Box>
            </Card>
          );
        })
      )}
      
      {/* Summary */}
      <Box mt={2} display="flex" gap={3}>
        {Object.entries(filteredUnitsByLevel).map(([levelId, units]) => {
          const level = hierarchyLevels.find(l => l.levelId === levelId);
          const activeCount = units.filter(u => u.isActive).length;
          const totalCount = units.length;
          
          return (
            <Typography key={levelId} variant="caption" color="text">
              {level ? level.label : levelId}: {activeCount} active{totalCount > activeCount ? ` / ${totalCount} total` : ''}
            </Typography>
          );
        })}
      </Box>

      {/* Edit Modal */}
      <EditOrgUnitModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedUnit(null);
        }}
        unit={selectedUnit}
        onSuccess={() => {
          // Refresh the data after successful edit
          refetchOrgUnits();
          setEditModalOpen(false);
          setSelectedUnit(null);
        }}
        hierarchyLevels={hierarchyLevels}
      />

      {/* Create Modal */}
      <CreateOrgUnitModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          // Refresh the data after successful creation
          refetchOrgUnits();
          setCreateModalOpen(false);
        }}
        hierarchyLevels={hierarchyLevels}
      />
    </Box>
  );
}

export default UnitsTab;